import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { workflowHasComponentOntology } from 'features/libra/ontology/useOntologyFields'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import type { WorkflowScopedProviderDescriptor } from 'features/libra/workflow/workflowScopedProviderTypes'
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { buildFfsAssetModelSnapshotEntry } from './ffsAssetModelSnapshot'
import { useFfsAssetModelSnapshot } from './useFfsAssetModelSnapshot'

interface FfsAssetModelSnapshotContextValue {
  /**
   * True when the single-owner lazy snapshot create failed (e.g. missing
   * write permission). Reads fall back to the live model -- identical to
   * pre-snapshot behavior -- and `FfsAssetModelStep` surfaces a warning.
   * Owned here, NOT per hook instance, so every consumer sees the same
   * answer regardless of mount order.
   */
  snapshotCreateFailed: boolean
}

const DEFAULT_VALUE: FfsAssetModelSnapshotContextValue = {
  snapshotCreateFailed: false,
}

const FfsAssetModelSnapshotContext =
  createContext<FfsAssetModelSnapshotContextValue>(DEFAULT_VALUE)

/**
 * Single owner of ticket asset-model snapshot CREATION. Injected once per
 * ticket page via the workflow-scoped provider registry (see the descriptor
 * below) -- ONLY for asset-scoped workflows, so non-FFS workflows never
 * mount it and pay zero fetch cost. Reading stays in
 * `useFfsAssetModelSnapshot` -- a pure, SWR-deduped hook -- so this
 * provider is the only place a `rolodexPut` of the snapshot can originate:
 * no cross-mount create races, no module-level dedupe registry, and one
 * deterministic failure flag.
 */
export const FfsAssetModelSnapshotProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { ticketId, readOnly } = useTicketAccess()
  const { assetModel, isSnapshot, snapshotId, isLoading, forceRefresh } =
    useFfsAssetModelSnapshot()
  const { rolodexPut } = useRolodexEdit()
  const [snapshotCreateFailed, setSnapshotCreateFailed] = useState(false)

  // One attempt per mount (the page keys on ticket.id, so per ticket open).
  // Also guards a retry loop if the put succeeds but the refresh still
  // resolves empty (eventual consistency) -- we fall back to the live read
  // rather than hammer Rolodex. Cross-user/tab races are benign: the id is
  // deterministic and both writers copy the same live model.
  const attemptedRef = useRef(false)

  useEffect(() => {
    if (attemptedRef.current) return
    if (readOnly || isLoading || isSnapshot) return
    // No live model to copy yet (unit has no asset model) -- the Asset
    // Model step's existing warning covers this; snapshot on a later open.
    if (!assetModel) return
    attemptedRef.current = true
    rolodexPut([
      buildFfsAssetModelSnapshotEntry({ ticketId, liveAssetModel: assetModel }),
    ])
      .then(() => forceRefresh())
      .catch((error) => {
        console.warn(
          `[Libra FFS] Failed to create asset-model snapshot "${snapshotId}"; falling back to the live asset model.`,
          error
        )
        setSnapshotCreateFailed(true)
      })
  }, [
    readOnly,
    isLoading,
    isSnapshot,
    assetModel,
    ticketId,
    snapshotId,
    rolodexPut,
    forceRefresh,
  ])

  const value = useMemo(
    () => ({ snapshotCreateFailed }),
    [snapshotCreateFailed]
  )

  return (
    <FfsAssetModelSnapshotContext.Provider value={value}>
      {children}
    </FfsAssetModelSnapshotContext.Provider>
  )
}

/**
 * Whether the single-owner snapshot create failed for this ticket page.
 * Safe anywhere under `LibraView`; returns false (the context default) when
 * the workflow isn't asset-scoped and the provider is not mounted.
 */
export function useSnapshotCreateFailed(): boolean {
  return useContext(FfsAssetModelSnapshotContext).snapshotCreateFailed
}

/**
 * Registry descriptor: the libra shell injects the snapshot provider (via
 * `WorkflowScopedProviders placement='ticket'`) only for workflows that
 * declare component ontology -- the same asset-scoped marker the snapshot
 * read hook and the tabbed UI key on.
 */
export const ffsAssetModelSnapshotScopedProvider: WorkflowScopedProviderDescriptor =
  {
    key: 'ffs-asset-model-snapshot',
    placement: 'ticket',
    appliesTo: workflowHasComponentOntology,
    Provider: FfsAssetModelSnapshotProvider,
  }
